import type { DependencyGraph, DependencyNode } from "../models/DependencyGraph.js";

const TYPE_ICONS: Record<string, string> = {
  native: "[N]",
  plugin: "[P]",
  production: "[·]",
  dev: "[D]",
};

function nodeLabel(node: DependencyNode): string {
  const icon = TYPE_ICONS[node.type] ?? "[·]";
  const tags: string[] = [];
  if (node.isNative) tags.push("native");
  if (node.hasPlugin) tags.push("plugin");
  const tagStr = tags.length ? ` (${tags.join(", ")})` : "";
  return `${icon} ${node.name}@${node.version}${tagStr}`;
}

/**
 * Renders a dependency graph as human-readable text.
 */
export function exportGraphAsText(graph: DependencyGraph): string {
  const lines: string[] = [];

  lines.push(`Dependency Graph — ${graph.projectName}`);
  lines.push(`React Native: ${graph.reactNativeVersion}`);
  lines.push(
    `${graph.totalNodes} packages  |  ${graph.nativeCount} native  |  ${graph.pluginCount} with plugins`
  );
  lines.push("");
  lines.push("Legend: [N]=Native  [P]=Has Plugin  [·]=Production  [D]=Dev");
  lines.push("─".repeat(60));

  // Group by type
  const native = graph.nodes.filter((n) => n.isNative);
  const withPlugin = graph.nodes.filter((n) => n.hasPlugin && !n.isNative);
  const production = graph.nodes.filter((n) => n.type === "production" && !n.isNative && !n.hasPlugin);
  const dev = graph.nodes.filter((n) => n.type === "dev");

  if (native.length > 0) {
    lines.push("");
    lines.push("Native Modules");
    for (const node of native) {
      lines.push(`  ${nodeLabel(node)}`);
    }
  }

  if (withPlugin.length > 0) {
    lines.push("");
    lines.push("Packages with Plugin Support");
    for (const node of withPlugin) {
      lines.push(`  ${nodeLabel(node)}`);
    }
  }

  if (production.length > 0) {
    lines.push("");
    lines.push("Production Dependencies");
    for (const node of production) {
      lines.push(`  ${nodeLabel(node)}`);
    }
  }

  if (dev.length > 0) {
    lines.push("");
    lines.push("Dev Dependencies");
    for (const node of dev) {
      lines.push(`  ${nodeLabel(node)}`);
    }
  }

  lines.push("");
  lines.push(`Generated: ${graph.generatedAt}`);

  return lines.join("\n");
}

/**
 * Renders a dependency graph as a JSON string.
 */
export function exportGraphAsJson(graph: DependencyGraph): string {
  return JSON.stringify(graph, null, 2);
}
